import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import DatePickerModal from './modals/DatePickerModal';
import GuestSelectorModal from './modals/GuestSelectorModal';
import { useDateContext } from '../contexts/DateContext';
import { useGuestContext } from '../contexts/GuestContext';
import { getStoredCity, setStoredCity } from '../utils/locationPreference';
import { useStayCities } from '../contexts/StayCitiesContext';
import {
  ALL_LOCATIONS_LABEL,
  canonicalCityLabel,
  cityPickerOptions,
  displayCityLabel,
  isAllLocationsSelection,
  normalizeCityKey,
} from '../utils/cityUtils';
import { dateToYmd } from '../utils/dateYmd';

const BookingWidget = () => {
  const navigate = useNavigate();
  const { selectedDates, updateDates } = useDateContext();
  const { guestData, formatGuestString } = useGuestContext();
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showGuestSelector, setShowGuestSelector] = useState(false);
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);
  const { cities: stayCities, loading: citiesLoading } = useStayCities();

  const [searchData, setSearchData] = useState({
    location: getStoredCity(),
    checkIn: selectedDates.checkIn,
    checkOut: selectedDates.checkOut,
    guests: formatGuestString().fullString,
    // Parse guest data for search
    roomsCount: guestData.rooms,
    adultsCount: guestData.adults,
    childrenCount: guestData.children,
    petsCount: guestData.pets,
    // Store actual Date objects for proper year handling
    checkInDate: selectedDates.checkInDate,
    checkOutDate: selectedDates.checkOutDate
  });

  // Sync searchData with global dates when they change
  React.useEffect(() => {
    setSearchData(prev => ({
      ...prev,
      checkIn: selectedDates.checkIn,
      checkOut: selectedDates.checkOut,
      checkInDate: selectedDates.checkInDate,
      checkOutDate: selectedDates.checkOutDate
    }));
  }, [selectedDates]);

  // Sync searchData with global guests when they change
  React.useEffect(() => {
    setSearchData(prev => ({
      ...prev,
      guests: formatGuestString().fullString,
      roomsCount: guestData.rooms,
      adultsCount: guestData.adults,
      childrenCount: guestData.children,
      petsCount: guestData.pets
    }));
  }, [guestData, formatGuestString]);

  React.useEffect(() => {
    const onCityChange = (e) => {
      const city = e.detail?.city;
      if (city) setSearchData((prev) => ({ ...prev, location: city }));
    };
    window.addEventListener('bfp:city-changed', onCityChange);
    return () => window.removeEventListener('bfp:city-changed', onCityChange);
  }, []);

  const selectedCityKey = normalizeCityKey(searchData.location);

  const cityOptions = useMemo(
    () => cityPickerOptions(stayCities, searchData.location),
    [stayCities, searchData.location]
  );

  useEffect(() => {
    if (!cityDropdownOpen) return;
    const onPointerDown = (e) => {
      const roots = document.querySelectorAll('[data-booking-city-picker]');
      for (let i = 0; i < roots.length; i++) {
        if (roots[i].contains(e.target)) return;
      }
      setCityDropdownOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [cityDropdownOpen]);

  const handleSelectCity = (city) => {
    const label = isAllLocationsSelection(city)
      ? ALL_LOCATIONS_LABEL
      : canonicalCityLabel(city) || city;
    setSearchData((prev) => ({ ...prev, location: label }));
    setStoredCity(label);
    setCityDropdownOpen(false);
  };

  // Custom Map Pin Icon for the widget
  const MapPinIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
      <g opacity="0.4" clip-path="url(#clip0_250_22936)">
        <path d="M9 11C9 11.7956 9.31607 12.5587 9.87868 13.1213C10.4413 13.6839 11.2044 14 12 14C12.7956 14 13.5587 13.6839 14.1213 13.1213C14.6839 12.5587 15 11.7956 15 11C15 10.2044 14.6839 9.44129 14.1213 8.87868C13.5587 8.31607 12.7956 8 12 8C11.2044 8 10.4413 8.31607 9.87868 8.87868C9.31607 9.44129 9 10.2044 9 11Z" stroke="black" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M17.657 16.657L13.414 20.9C13.039 21.2746 12.5306 21.4851 12.0005 21.4851C11.4704 21.4851 10.962 21.2746 10.587 20.9L6.343 16.657C5.22422 15.5382 4.46234 14.1127 4.15369 12.5609C3.84504 11.009 4.00349 9.40053 4.60901 7.93874C5.21452 6.47696 6.2399 5.22755 7.55548 4.34852C8.87107 3.46949 10.4178 3.00031 12 3.00031C13.5822 3.00031 15.1289 3.46949 16.4445 4.34852C17.7601 5.22755 18.7855 6.47696 19.391 7.93874C19.9965 9.40053 20.155 11.009 19.8463 12.5609C19.5377 14.1127 18.7758 15.5382 17.657 16.657Z" stroke="black" stroke-linecap="round" stroke-linejoin="round" />
      </g>
      <defs>
        <clipPath id="clip0_250_22936">
          <rect width="24" height="24" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
  const CalendarIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
      <g clip-path="url(#clip0_174_2661)">
        <path d="M4 7C4 6.46957 4.21071 5.96086 4.58579 5.58579C4.96086 5.21071 5.46957 5 6 5H18C18.5304 5 19.0391 5.21071 19.4142 5.58579C19.7893 5.96086 20 6.46957 20 7V19C20 19.5304 19.7893 20.0391 19.4142 20.4142C19.0391 20.7893 18.5304 21 18 21H6C5.46957 21 4.96086 20.7893 4.58579 20.4142C4.21071 20.0391 4 19.5304 4 19V7Z" stroke="black" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M16 3V7" stroke="black" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M8 3V7" stroke="black" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M4 11H20" stroke="black" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M8 15H10V17H8V15Z" stroke="black" stroke-linecap="round" stroke-linejoin="round" />
      </g>
      <defs>
        <clipPath id="clip0_174_2661">
          <rect width="24" height="24" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );

  const UsersIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
      <g clip-path="url(#clip0_174_2684)">
        <path d="M5 7C5 8.06087 5.42143 9.07828 6.17157 9.82843C6.92172 10.5786 7.93913 11 9 11C10.0609 11 11.0783 10.5786 11.8284 9.82843C12.5786 9.07828 13 8.06087 13 7C13 5.93913 12.5786 4.92172 11.8284 4.17157C11.0783 3.42143 10.0609 3 9 3C7.93913 3 6.92172 3.42143 6.17157 4.17157C5.42143 4.92172 5 5.93913 5 7Z" stroke="black" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M3 21V19C3 17.9391 3.42143 16.9217 4.17157 16.1716C4.92172 15.4214 5.93913 15 7 15H11C12.0609 15 13.0783 15.4214 13.8284 16.1716C14.5786 16.9217 15 17.9391 15 19V21" stroke="black" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="black" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M21 21V19C20.9949 18.1172 20.6979 17.2608 20.1553 16.5644C19.6126 15.868 18.8548 15.3707 18 15.15" stroke="black" stroke-linecap="round" stroke-linejoin="round" />
      </g>
      <defs>
        <clipPath id="clip0_174_2684">
          <rect width="24" height="24" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );

  // Function to parse and format dates for API
  const formatDateForAPI = (dateString) => {
    if (!dateString) return '';

    // Handle different date formats
    const currentYear = new Date().getFullYear();

    // If date is in format "Wed, 10 Jun" or "Tue, Sep 2", convert to YYYY-MM-DD
    const months = {
      'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04',
      'May': '05', 'Jun': '06', 'Jul': '07', 'Aug': '08',
      'Sep': '09', 'Sept': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
    };

    // Try to parse the date string manually first
    const parts = dateString.replace(/,/g, '').split(' ');
    if (parts.length >= 3) {
      const day = parts[1].padStart(2, '0');
      const month = months[parts[2]];
      if (month) {
        // Check if the date would be in the past
        const proposedDate = new Date(`${currentYear}-${month}-${day}`);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // If the date is in the past, use next year
        if (proposedDate < today) {
          return `${currentYear + 1}-${month}-${day}`;
        }

        return `${currentYear}-${month}-${day}`;
      }
    }

    // If manual parsing fails, try to use Date constructor with current year context
    try {
      // Add current year to the date string if it doesn't have one
      let dateToParse = dateString;
      if (!dateString.match(/\d{4}/)) {
        // If no year in the string, append current year
        dateToParse = `${dateString} ${currentYear}`;
      }

      const date = new Date(dateToParse);
      if (!isNaN(date.getTime())) {
        // Ensure the year is reasonable (not in the past)
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (date < today) {
          // If date is in the past, try next year
          const nextYearDate = new Date(dateToParse.replace(/\d{4}/, (currentYear + 1).toString()));
          if (!isNaN(nextYearDate.getTime())) {
            return nextYearDate.toISOString().split('T')[0];
          }
        }

        return date.toISOString().split('T')[0];
      }
    } catch (e) {
      console.warn('Failed to parse date:', dateString);
    }

    return dateString;
  };

  // Function to handle search
  const handleSearch = () => {
    // Use Date objects directly if available, otherwise fall back to string parsing
    let checkInFormatted = '';
    let checkOutFormatted = '';

    if (searchData.checkInDate && searchData.checkOutDate) {
      checkInFormatted = dateToYmd(searchData.checkInDate) || '';
      checkOutFormatted = dateToYmd(searchData.checkOutDate) || '';
      console.log('Using Date objects:', { checkInFormatted, checkOutFormatted });
    } else {
      // Fall back to string parsing (for backward compatibility)
      checkInFormatted = formatDateForAPI(searchData.checkIn);
      checkOutFormatted = formatDateForAPI(searchData.checkOut);
      console.log('Using string parsing:', { checkInFormatted, checkOutFormatted });
    }

    // Calculate total guests
    const totalGuests = searchData.adultsCount + searchData.childrenCount;

    // Create search parameters
    const searchParams = new URLSearchParams({
      ...(!isAllLocationsSelection(searchData.location) && {
        location: canonicalCityLabel(searchData.location) || searchData.location,
      }),
      ...(checkInFormatted && { checkIn: checkInFormatted }),
      ...(checkOutFormatted && { checkOut: checkOutFormatted }),
      guests: totalGuests.toString(),
      rooms: searchData.roomsCount.toString(),
      adults: searchData.adultsCount.toString(),
      children: searchData.childrenCount.toString(),
      pets: searchData.petsCount.toString()
    });

    console.log('Search params:', searchParams.toString());

    // Navigate to search results
    navigate(`/search-results?${searchParams.toString()}`);
  };

  return (
    <>
      <div className="bg-white rounded-[24px] p-[16px]  relative z-10 mx-[16px] md:mx-[60px] lg:mx-[120px]">
        <div className="flex flex-col lg:flex-row items-center space-y-4 lg:space-y-0 lg:space-x-[8px]">
          {/* Location Input */}
          <div
            className="relative flex-1 w-full lg:w-auto"
            data-booking-city-picker
          >
            <button
              type="button"
              onClick={() => setCityDropdownOpen((o) => !o)}
              className="flex items-center p-[16px] rounded-[12px] cursor-pointer border-[1px] border-[#ededed] space-x-[12px] w-full text-left"
              aria-expanded={cityDropdownOpen}
              aria-haspopup="listbox"
            >
              <MapPinIcon />
              <span className="text-black text-[18px] font-normal flex-1 min-w-0 truncate">
                {displayCityLabel(searchData.location)}
              </span>
              <ChevronDown
                className={`w-5 h-5 text-black/50 shrink-0 transition-transform duration-200 ${cityDropdownOpen ? 'rotate-180' : ''}`}
                aria-hidden
              />
            </button>
            {cityDropdownOpen && (
              <ul
                role="listbox"
                className="absolute left-0 right-0 top-full mt-1 z-[60] max-h-[min(280px,50vh)] overflow-y-auto rounded-xl bg-white shadow-lg border border-[#ededed] py-1.5"
              >
                {citiesLoading && (
                  <li className="px-4 py-3 text-sm text-gray-500">Loading cities…</li>
                )}
                {!citiesLoading &&
                  cityOptions.map((city) => (
                    <li
                      key={normalizeCityKey(city)}
                      role="option"
                      aria-selected={normalizeCityKey(city) === selectedCityKey}
                    >
                      <button
                        type="button"
                        className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                          normalizeCityKey(city) === selectedCityKey
                            ? 'bg-[#FF5E4C]/12 text-[#FF5E4C] font-semibold'
                            : 'text-gray-800 hover:bg-black/5'
                        }`}
                        onClick={() => handleSelectCity(city)}
                      >
                        {city}
                      </button>
                    </li>
                  ))}
              </ul>
            )}
          </div>

          {/* Dates Input */}
          <div
            className="flex items-center p-[16px] border-[1px] border-[#ededed] rounded-[12px] cursor-pointer flex-1 w-full lg:w-auto"
            onClick={() => setShowDatePicker(true)}
          >

            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-[4px]">
                <CalendarIcon />
                <span className="text-black text-[18px] font-normal">{searchData.checkIn}</span>
              </div>
              <div className="w-6 h-px bg-black"></div>
              <div className="flex items-center gap-[4px]">
                <CalendarIcon />
                <span className="text-black text-[18px] font-normal">{searchData.checkOut}</span>
              </div>
            </div>
          </div>


          {/* Guests Input */}
          <div
            className="flex items-center p-[16px] border-[1px] border-[#ededed] rounded-[12px] cursor-pointer space-x-[12px] flex-1 w-full lg:w-auto"
            onClick={() => setShowGuestSelector(true)}
          >
            <UsersIcon />
            <div className="flex flex-wrap items-center space-x-[12px]">
              <span className="text-black text-[18px] font-normal">{searchData.roomsCount} Room{searchData.roomsCount !== 1 ? 's' : ''}</span>
              <span className="text-black text-[18px] font-normal">{searchData.adultsCount} Adult{searchData.adultsCount !== 1 ? 's' : ''}</span>
              {searchData.childrenCount > 0 && (
                <span className="text-black text-[18px] font-normal">{searchData.childrenCount} Child{searchData.childrenCount !== 1 ? 'ren' : ''}</span>
              )}
              {searchData.petsCount > 0 && (
                <span className="text-black text-[18px] font-normal">{searchData.petsCount} Pet{searchData.petsCount !== 1 ? 's' : ''}</span>
              )}
            </div>
          </div>



          {/* Search Button */}
          <button
            onClick={handleSearch}
            className="bg-black relative overflow-hidden cursor-pointer text-white px-[23px] py-[18px] md:py-[14px] rounded-[12px] font-bold text-[16px] cursor-pointer flex-none w-full lg:w-[160px] "
          >
            Search
            <svg className='block md:hidden absolute right-[10%] bottom-[-5%]' xmlns="http://www.w3.org/2000/svg" width="61" height="36" viewBox="0 0 61 36" fill="none">
              <path d="M17.8291 8.93766C17.4553 8.93766 17.0741 8.84601 16.7368 8.76537C15.8388 8.5491 13.7384 8.04692 14.2406 5.94657C14.4348 5.13283 14.8784 4.42354 15.4887 3.94885C16.1375 3.44301 16.9274 3.25424 17.6623 3.43019C19.1322 3.78208 19.99 5.48471 19.5739 7.22401C19.244 8.60224 18.5476 8.93766 17.8291 8.93766ZM17.1107 4.83226C16.7955 4.83226 16.5315 4.99538 16.3886 5.10717C16.0458 5.37476 15.7819 5.80545 15.6665 6.2893C15.5602 6.731 15.5748 6.98025 17.0777 7.33947C17.3123 7.39629 17.8053 7.51176 17.9171 7.46594C17.9171 7.46594 18.0289 7.37613 18.1462 6.88311C18.3698 5.94657 17.9923 5.0192 17.3196 4.85792C17.2482 4.84143 17.1785 4.83226 17.1107 4.83226Z" fill="#808080" />
              <path d="M9.68653 8.10003C9.35114 8.10003 9.01389 8.05238 8.70599 7.90026C8.1305 7.61435 7.79693 7.04986 7.68879 6.17929C7.58982 5.38204 7.74927 4.59395 8.13781 3.95982C8.55385 3.28352 9.20448 2.84365 9.92659 2.75568C10.6432 2.66587 11.3855 2.93164 11.9555 3.48514C12.4888 4.00381 12.8352 4.72956 12.9342 5.52682C13.0716 6.62648 12.5694 7.21665 12.1222 7.51722C11.6182 7.85811 10.9695 7.97539 10.3995 8.04687C10.1704 8.07619 9.92846 8.10003 9.68287 8.10003H9.68653ZM10.2088 4.20173C10.1759 4.20173 10.1429 4.20357 10.108 4.20907C9.72865 4.25672 9.49224 4.55362 9.38777 4.7259C9.17517 5.07229 9.08718 5.53416 9.144 5.99602C9.18432 6.31858 9.26499 6.53851 9.35846 6.58616C9.53807 6.67597 10.0347 6.61366 10.2217 6.58983C10.5956 6.54401 11.0556 6.46888 11.303 6.30026C11.3891 6.24161 11.5358 6.14264 11.4808 5.70461C11.424 5.24275 11.2242 4.81388 10.9328 4.53164C10.8008 4.40334 10.5406 4.1999 10.2088 4.1999V4.20173Z" fill="#808080" />
              <path d="M4.47307 12.2714C3.619 12.2714 3.12602 11.7308 2.90609 11.4045C2.08684 10.1839 2.35989 8.55825 3.51637 7.78115C4.67284 7.00589 6.28202 7.36512 7.1031 8.58574C7.52281 9.21071 7.6291 9.81002 7.422 10.369C7.21123 10.939 6.72188 11.3221 6.31867 11.5933C5.57274 12.0937 4.96425 12.2714 4.47307 12.2714ZM4.87631 8.83499C4.6857 8.83499 4.49689 8.88815 4.33377 8.99628C3.84809 9.32251 3.75281 10.0355 4.12302 10.5853C4.22199 10.7337 4.45659 11.0765 5.50127 10.3745C5.91181 10.0996 6.01994 9.93281 6.04743 9.85767C6.08958 9.74221 5.9833 9.5461 5.88617 9.40131C5.64058 9.03659 5.25203 8.83316 4.87631 8.83316V8.83499Z" fill="#808080" />
              <path d="M22.6033 13.0027C21.9069 13.0027 21.2874 12.5867 20.8989 12.3264C20.2739 11.9049 18.3678 10.6256 19.6837 8.66823C20.7192 7.12687 22.6198 6.58254 23.9193 7.45677C25.2187 8.331 25.435 10.2939 24.3995 11.8353C23.8056 12.7205 23.1788 13.0027 22.6033 13.0027ZM22.5117 8.50146C21.9545 8.50146 21.3149 8.86618 20.9007 9.48382C20.591 9.94568 20.492 10.2847 21.7163 11.1077C22.6235 11.7161 22.7463 11.6666 23.1825 11.016C23.758 10.1601 23.7195 9.0861 23.1018 8.6719C22.9296 8.55643 22.7261 8.50146 22.5117 8.50146Z" fill="#808080" />
              <path d="M22.7727 42.5084H4.44509C4.24348 42.5084 4.05106 42.4259 3.91177 42.2793C3.77431 42.1327 3.70098 41.9348 3.71198 41.735C3.71931 41.603 4.44325 28.4933 4.44325 22.7146C4.44325 18.8547 3.80547 16.3274 3.36744 15.0444C2.50604 14.5423 1.74727 13.822 1.16079 12.9496C-0.642656 10.2646 -0.307292 6.8703 1.90669 5.3821C2.92021 4.70214 4.20499 4.51885 5.48976 4.84325C5.75001 2.29754 7.38668 0.307158 9.58417 0.0322431C11.2776 -0.180358 12.9088 0.670053 13.9828 2.2389C15.1979 1.03661 16.7686 0.521594 18.2715 0.882649C20.0181 1.30235 21.3157 2.84371 21.6914 4.85059C22.9909 4.5042 24.2701 4.68748 25.3075 5.38393C27.5215 6.87214 27.8568 10.2664 26.0534 12.9514C25.4669 13.8256 24.7082 14.5441 23.8468 15.0463C23.4069 16.3274 22.7691 18.8529 22.7691 22.7164C22.7691 28.4933 23.493 41.6049 23.5003 41.7368C23.5113 41.9384 23.4398 42.1345 23.3006 42.2812C23.1613 42.4278 22.9688 42.5103 22.7672 42.5103L22.7727 42.5084ZM5.21851 41.0422H21.9975C21.8417 38.1244 21.3047 27.7125 21.3047 22.7146C21.3047 18.3507 22.0928 15.5594 22.562 14.2838C22.6243 14.1134 22.7489 13.9723 22.912 13.888C23.6635 13.4958 24.3288 12.8891 24.8383 12.1322C26.1909 10.1198 26.035 7.6364 24.4919 6.60089C23.6415 6.0309 22.5052 5.99973 21.3725 6.5184C21.1508 6.62104 20.8923 6.60455 20.6816 6.47625C20.4708 6.34796 20.3425 6.12253 20.3333 5.87877C20.2673 4.07166 19.3015 2.63661 17.9288 2.30855C16.7063 2.0153 15.3757 2.63845 14.4611 3.93788C14.3108 4.15048 14.0616 4.2696 13.794 4.24577C13.5319 4.22194 13.3047 4.05884 13.1947 3.82058C12.4671 2.23524 11.1237 1.32069 9.76378 1.48747C8.0648 1.70007 6.81669 3.60981 6.92482 5.83845C6.93765 6.09504 6.81302 6.3388 6.60042 6.48359C6.38599 6.62655 6.11109 6.64669 5.88016 6.53673C4.73651 5.99973 3.58737 6.02174 2.72231 6.60089C1.17912 7.63824 1.02331 10.1198 2.37589 12.1322C2.8854 12.8891 3.55069 13.4976 4.30213 13.888C4.46341 13.9723 4.58802 14.1134 4.65217 14.2857C5.12136 15.5613 5.90947 18.3544 5.90947 22.7146C5.90947 27.7125 5.37246 38.1244 5.21667 41.0422H5.21851Z" fill="#808080" />
              <path d="M16.1762 19.7822C15.3551 19.7822 14.9995 19.5824 14.6495 19.3405C14.4314 19.1902 14.2261 19.0491 13.2438 19.0491C12.2614 19.0491 12.0561 19.1902 11.838 19.3405C11.488 19.5824 11.1324 19.7822 10.3113 19.7822C9.29598 19.7822 7.37891 18.8621 7.37891 15.3835C7.37891 12.134 10.5881 9.51868 13.2438 9.51868C15.8994 9.51868 19.1086 12.134 19.1086 15.3835C19.1086 18.8621 17.1915 19.7822 16.1762 19.7822ZM13.2438 17.5828C14.5743 17.5828 15.0454 17.8321 15.4816 18.1345C15.6612 18.2591 15.7436 18.316 16.1762 18.316C16.5849 18.316 17.6424 17.7881 17.6424 15.3835C17.6424 12.8323 15.0142 10.9849 13.2438 10.9849C11.4733 10.9849 8.84512 12.8323 8.84512 15.3835C8.84512 17.7881 9.90262 18.316 10.3113 18.316C10.742 18.316 10.8245 18.2591 11.006 18.1345C11.444 17.8339 11.9132 17.5828 13.2438 17.5828Z" fill="#808080" />
              <path d="M42.3818 8.93766C42.7557 8.93766 43.1369 8.84601 43.4741 8.76537C44.3722 8.5491 46.4725 8.04692 45.9704 5.94657C45.7761 5.13283 45.3325 4.42354 44.7222 3.94885C44.0734 3.44301 43.2835 3.25424 42.5486 3.43019C41.0787 3.78208 40.221 5.48471 40.637 7.22401C40.9669 8.60224 41.6633 8.93766 42.3818 8.93766ZM43.1002 4.83226C43.4155 4.83226 43.6794 4.99538 43.8224 5.10717C44.1651 5.37476 44.429 5.80545 44.5445 6.2893C44.6508 6.731 44.6361 6.98025 43.1332 7.33947C42.8986 7.39629 42.4056 7.51176 42.2938 7.46594C42.2938 7.46594 42.182 7.37613 42.0647 6.88311C41.8411 5.94657 42.2187 5.0192 42.8913 4.85792C42.9628 4.84143 43.0324 4.83226 43.1002 4.83226Z" fill="#808080" />
              <path d="M50.5283 8.10003C50.8637 8.10003 51.201 8.05238 51.5089 7.90026C52.0843 7.61435 52.4179 7.04986 52.526 6.17929C52.625 5.38204 52.4656 4.59395 52.077 3.95982C51.661 3.28352 51.0104 2.84365 50.2883 2.75568C49.5716 2.66587 48.8294 2.93164 48.2594 3.48514C47.726 4.00381 47.3796 4.72956 47.2807 5.52682C47.1432 6.62648 47.6454 7.21665 48.0926 7.51722C48.5966 7.85811 49.2454 7.97539 49.8154 8.04687C50.0445 8.07619 50.2864 8.10003 50.532 8.10003H50.5283ZM50.006 4.20173C50.039 4.20173 50.072 4.20357 50.1068 4.20907C50.4862 4.25672 50.7226 4.55362 50.8271 4.7259C51.0397 5.07229 51.1277 5.53416 51.0708 5.99602C51.0305 6.31858 50.9499 6.53851 50.8564 6.58616C50.6768 6.67597 50.1801 6.61366 49.9932 6.58983C49.6193 6.54401 49.1593 6.46888 48.9118 6.30026C48.8257 6.24161 48.6791 6.14264 48.7341 5.70461C48.7909 5.24275 48.9907 4.81388 49.2821 4.53164C49.414 4.40334 49.6743 4.1999 50.006 4.1999V4.20173Z" fill="#808080" />
              <path d="M55.7418 12.2714C56.5958 12.2714 57.0888 11.7308 57.3088 11.4045C58.128 10.1839 57.855 8.55825 56.6985 7.78115C55.542 7.00589 53.9328 7.36512 53.1117 8.58574C52.692 9.21071 52.5857 9.81002 52.7928 10.369C53.0036 10.939 53.493 11.3221 53.8962 11.5933C54.6421 12.0937 55.2506 12.2714 55.7418 12.2714ZM55.3385 8.83499C55.5291 8.83499 55.718 8.88815 55.8811 8.99628C56.3668 9.32251 56.462 10.0355 56.0918 10.5853C55.9929 10.7337 55.7583 11.0765 54.7136 10.3745C54.303 10.0996 54.1949 9.93281 54.1674 9.85767C54.1253 9.74221 54.2315 9.5461 54.3287 9.40131C54.5743 9.03659 54.9628 8.83316 55.3385 8.83316V8.83499Z" fill="#808080" />
              <path d="M37.6115 13.0027C38.308 13.0027 38.9274 12.5867 39.316 12.3264C39.941 11.9049 41.8471 10.6256 40.5311 8.66823C39.4956 7.12687 37.595 6.58254 36.2956 7.45677C34.9962 8.331 34.7799 10.2939 35.8154 11.8353C36.4092 12.7205 37.036 13.0027 37.6115 13.0027ZM37.7031 8.50146C38.2603 8.50146 38.8999 8.86618 39.3142 9.48382C39.6239 9.94568 39.7229 10.2847 38.4986 11.1077C37.5914 11.7161 37.4686 11.6666 37.0324 11.016C36.4569 10.1601 36.4954 9.0861 37.113 8.6719C37.2853 8.55643 37.4887 8.50146 37.7031 8.50146Z" fill="#808080" />
              <path d="M37.4421 42.5084H55.7698C55.9714 42.5084 56.1638 42.4259 56.3031 42.2793C56.4405 42.1327 56.5139 41.9348 56.5029 41.735C56.4955 41.603 55.7716 28.4933 55.7716 22.7146C55.7716 18.8547 56.4094 16.3274 56.8474 15.0444C57.7088 14.5423 58.4676 13.822 59.0541 12.9496C60.8575 10.2646 60.5221 6.8703 58.3082 5.3821C57.2946 4.70214 56.0099 4.51885 54.7251 4.84325C54.4648 2.29754 52.8282 0.307158 50.6307 0.0322431C48.9372 -0.180358 47.306 0.670053 46.232 2.2389C45.0169 1.03661 43.4463 0.521594 41.9434 0.882649C40.1968 1.30235 38.8991 2.84371 38.5234 4.85059C37.224 4.5042 35.9447 4.68748 34.9074 5.38393C32.6934 6.87214 32.358 10.2664 34.1615 12.9514C34.748 13.8256 35.5067 14.5441 36.3681 15.0463C36.8079 16.3274 37.4458 18.8529 37.4458 22.7164C37.4458 28.4933 36.7218 41.6049 36.7145 41.7368C36.7035 41.9384 36.775 42.1345 36.9143 42.2812C37.0536 42.4278 37.246 42.5103 37.4476 42.5103L37.4421 42.5084ZM54.9963 41.0422H38.2174C38.3731 38.1244 38.9101 27.7125 38.9101 22.7146C38.9101 18.3507 38.122 15.5594 37.6529 14.2838C37.5905 14.1134 37.4659 13.9723 37.3028 13.888C36.5514 13.4958 35.8861 12.8891 35.3766 12.1322C34.024 10.1198 34.1798 7.6364 35.723 6.60089C36.5734 6.0309 37.7097 5.99973 38.8423 6.5184C39.0641 6.62104 39.3225 6.60455 39.5333 6.47625C39.744 6.34796 39.8724 6.12253 39.8815 5.87877C39.9475 4.07166 40.9133 2.63661 42.2861 2.30855C43.5085 2.0153 44.8392 2.63845 45.7537 3.93788C45.904 4.15048 46.1532 4.2696 46.4208 4.24577C46.6829 4.22194 46.9102 4.05884 47.0201 3.82058C47.7477 2.23524 49.0912 1.32069 50.4511 1.48747C52.15 1.70007 53.3982 3.60981 53.29 5.83845C53.2772 6.09504 53.4018 6.3388 53.6144 6.48359C53.8289 6.62655 54.1038 6.64669 54.3347 6.53673C55.4783 5.99973 56.6275 6.02174 57.4925 6.60089C59.0357 7.63824 59.1915 10.1198 57.839 12.1322C57.3294 12.8891 56.6642 13.4976 55.9127 13.888C55.7514 13.9723 55.6268 14.1134 55.5627 14.2857C55.0935 15.5613 54.3054 18.3544 54.3054 22.7146C54.3054 27.7125 54.8424 38.1244 54.9982 41.0422H54.9963Z" fill="#808080" />
              <path d="M44.0387 19.7822C44.8597 19.7822 45.2153 19.5824 45.5654 19.3405C45.7835 19.1902 45.9887 19.0491 46.9711 19.0491C47.9534 19.0491 48.1587 19.1902 48.3768 19.3405C48.7269 19.5824 49.0824 19.7822 49.9035 19.7822C50.9189 19.7822 52.8359 18.8621 52.8359 15.3835C52.8359 12.134 49.6268 9.51868 46.9711 9.51868C44.3154 9.51868 41.1062 12.134 41.1062 15.3835C41.1062 18.8621 43.0233 19.7822 44.0387 19.7822ZM46.9711 17.5828C45.6405 17.5828 45.1695 17.8321 44.7333 18.1345C44.5537 18.2591 44.4712 18.316 44.0387 18.316C43.63 18.316 42.5724 17.7881 42.5724 15.3835C42.5724 12.8323 45.2006 10.9849 46.9711 10.9849C48.7415 10.9849 51.3697 12.8323 51.3697 15.3835C51.3697 17.7881 50.3122 18.316 49.9035 18.316C49.4728 18.316 49.3903 18.2591 49.2089 18.1345C48.7709 17.8339 48.3017 17.5828 46.9711 17.5828Z" fill="#808080" />
            </svg>
          </button>
        </div>
      </div>

      {/* Modals */}
      {showDatePicker && (
        <DatePickerModal
          onClose={() => setShowDatePicker(false)}
          initialDates={{
            checkInDate: searchData.checkInDate,
            checkOutDate: searchData.checkOutDate
          }}
          onOpenGuestSelector={() => setShowGuestSelector(true)}
          onSelect={(dates) => {
            console.log('DatePickerModal selected dates:', dates);
            // Global context is already updated by DatePickerModal
            setSearchData({
              ...searchData,
              checkIn: dates.checkIn,
              checkOut: dates.checkOut,
              // Store the actual Date objects for proper year handling
              checkInDate: dates.checkInDate,
              checkOutDate: dates.checkOutDate
            });
          }}
        />
      )}

      {showGuestSelector && (
        <GuestSelectorModal
          onClose={() => setShowGuestSelector(false)}
          onSelect={(guestString) => {
            // Global context is already updated by GuestSelectorModal
            // Just update local searchData to reflect the change
            setSearchData({
              ...searchData,
              guests: guestString,
              roomsCount: guestData.rooms,
              adultsCount: guestData.adults,
              childrenCount: guestData.children,
              petsCount: guestData.pets
            });
          }}
        />
      )}
    </>
  );
};

export default BookingWidget; 